import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Stats {
  totalPV: number;       // 全部历史总PV
  totalUV: number;       // 近30天UV
  todayPV: number;       // 今日PV
  todayUV: number;       // 今日UV
  pv30: number;          // 近30天PV
  pvGrowth: number;      // PV环比增长
}

export default function OverviewCards() {
  const [stats, setStats] = useState<Stats>({ totalPV: 0, totalUV: 0, todayPV: 0, todayUV: 0, pv30: 0, pvGrowth: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString();

        // 全部历史总PV
        const { count: allPV } = await supabase
          .from('site_visits')
          .select('*', { count: 'exact', head: true });

        // 近30天PV
        const { count: pv30 } = await supabase
          .from('site_visits')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', thirtyDaysAgo);

        // 前30天PV（用于环比）
        const { count: pvPrev } = await supabase
          .from('site_visits')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', sixtyDaysAgo)
          .lt('created_at', thirtyDaysAgo);

        // 近30天UV（独立session）
        const { data: uvData } = await supabase
          .from('site_visits')
          .select('session_id')
          .gte('created_at', thirtyDaysAgo)
          .not('session_id', 'is', null);
        const uv30 = new Set((uvData || []).map((r) => r.session_id)).size;

        // 今日PV + UV
        const { data: todayData } = await supabase
          .from('site_visits')
          .select('session_id')
          .gte('created_at', `${todayStr}T00:00:00.000Z`);
        const todayPV = todayData?.length || 0;
        const todayUV = new Set((todayData || []).map((r) => r.session_id)).size;

        const pvGrowth = (pvPrev || 0) > 0
          ? Math.round((((pv30 || 0) - (pvPrev || 0)) / (pvPrev || 1)) * 100)
          : 0;

        setStats({
          totalPV: allPV || 0,
          totalUV: uv30,
          todayPV,
          todayUV,
          pv30: pv30 || 0,
          pvGrowth,
        });
      } catch (err) {
        console.error('OverviewCards fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const cards = [
    {
      label: '历史总浏览次数',
      value: stats.totalPV.toLocaleString(),
      sub: '全部时间累计浏览',
      icon: 'ri-eye-line',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      badge: null,
    },
    {
      label: '近30天浏览次数',
      value: stats.pv30.toLocaleString(),
      sub: '近30天页面浏览',
      icon: 'ri-bar-chart-line',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      badge: stats.pvGrowth !== 0 ? { val: stats.pvGrowth, positive: stats.pvGrowth > 0 } : null,
    },
    {
      label: '近30天访客人数',
      value: stats.totalUV.toLocaleString(),
      sub: '近30天独立访客',
      icon: 'ri-user-line',
      color: 'text-sky-600',
      bg: 'bg-sky-50',
      badge: null,
    },
    {
      label: '今日浏览次数',
      value: stats.todayPV.toLocaleString(),
      sub: '今日页面浏览（实时）',
      icon: 'ri-time-line',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      badge: { val: 0, label: '实时' },
    },
    {
      label: '今日访客人数',
      value: stats.todayUV.toLocaleString(),
      sub: '今日独立访客（实时）',
      icon: 'ri-user-heart-line',
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      badge: { val: 0, label: '实时' },
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${card.bg}`}>
              <i className={`${card.icon} text-lg ${card.color}`}></i>
            </div>
            {card.badge && card.badge.val !== 0 ? (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${card.badge.positive ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
                {card.badge.val > 0 ? '+' : ''}{card.badge.val}%
              </span>
            ) : card.badge ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-500 bg-gray-50">
                {card.badge.label || '实时'}
              </span>
            ) : null}
          </div>
          <div className="text-2xl font-bold text-gray-800 mb-0.5">
            {loading ? <div className="h-8 w-16 bg-gray-100 rounded animate-pulse"></div> : card.value}
          </div>
          <div className="text-xs font-medium text-gray-700 mb-0.5">{card.label}</div>
          <div className="text-xs text-gray-400">{card.sub}</div>
        </div>
      ))}
    </div>
  );
}
